import { FC, useEffect, useMemo, useState } from 'react'
import { SignInWithApple } from '@capacitor-community/apple-sign-in'
import { useNavigate } from 'react-router-dom'
import { useClerk, useSignIn, useSignUp, useUser } from '@clerk/clerk-react'

// COMPONENTS
import Card from '@components/atoms/card'
import ErrorModal from '@components/molecules/error-modal'
import Spinner from '@components/atoms/spinner'
import Switch from '@components/atoms/switch'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { platform } from '@utils/capacitor-helper'
import { useFixedSettings } from '@components/templates/fixed/_localStore/_hooks'
import { getErrorMessage } from '@utils/error-helper'
import { type SupportedOAuthProvider, supportedOAuthProviders } from '@utils/oauth'

const connectedAccountLinkAttemptKey = 'connected-account-link-attempt'

const externalAccountErrorMessages = {
    external_account_exists: (label: string) => `This ${label} account is already linked to another Splitcard account.`,
    identifier_already_exists: (label: string) => `The email on this ${label} account is already in use by another Splitcard user.`,
    oauth_identification_claimed: (label: string) => `This ${label} account is already linked to another Splitcard account.`,
    oauth_access_denied: (label: string) => `You cancelled ${label} sign in before the account could be connected.`,
    external_account_already_connected: (label: string) => `This ${label} account is already connected.`,
    oauth_error: (label: string) => `Something went wrong with ${label}. Please try again.`,
} as const

interface ExternalAccountVerificationErrorDetails {
    code?: string
    message?: string
    longMessage?: string
}

interface ExternalAccountVerificationDetails {
    status?: string
    error?: ExternalAccountVerificationErrorDetails
}

interface ConnectedAccountErrorState {
    provider: SupportedOAuthProvider
    message: string
}

function readConnectedAccountLinkAttempt(): SupportedOAuthProvider | null {
    if (typeof window === 'undefined') return null

    const value = window.sessionStorage.getItem(connectedAccountLinkAttemptKey)
    return supportedOAuthProviders.some((provider) => provider.provider === value)
        ? value as SupportedOAuthProvider
        : null
}

function writeConnectedAccountLinkAttempt(provider: SupportedOAuthProvider) {
    if (typeof window === 'undefined') return
    window.sessionStorage.setItem(connectedAccountLinkAttemptKey, provider)
}

function clearConnectedAccountLinkAttempt() {
    if (typeof window === 'undefined') return
    window.sessionStorage.removeItem(connectedAccountLinkAttemptKey)
}

function getExternalAccountVerification(account: unknown): ExternalAccountVerificationDetails | null {
    if (typeof account !== 'object' || account == null || !('verification' in account)) return null

    const verification = account.verification
    return typeof verification === 'object' && verification != null
        ? verification as ExternalAccountVerificationDetails
        : null
}

function getExternalAccountErrorMessage(provider: SupportedOAuthProvider, code?: string | null): string | null {
    const providerConfig = supportedOAuthProviders.find((item) => item.provider === provider)
    const label = providerConfig?.label || 'social'
    if (!code) return null

    const knownMessage = externalAccountErrorMessages[code as keyof typeof externalAccountErrorMessages]
    return knownMessage ? knownMessage(label) : null
}

function getVerificationErrorFallbackMessage(error?: ExternalAccountVerificationErrorDetails | null): string | null {
    if (!error) return null

    if (typeof error.longMessage === 'string' && error.longMessage.trim().length > 0) {
        return error.longMessage.trim()
    }

    if (typeof error.message === 'string' && error.message.trim().length > 0) {
        return error.message.trim()
    }

    return null
}


function createNonce(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function isUserCancelledAppleSignIn(error: unknown): boolean {
    if (typeof error !== 'object' || error == null) return false
    const code = ('code' in error && typeof error.code === 'string') ? error.code.toLowerCase() : ''
    const message = ('message' in error && typeof error.message === 'string') ? error.message.toLowerCase() : ''
    return code.includes('cancel') || code.includes('request_canceled') || message.includes('cancel')
}

const Component: FC = () => {
    const navigate = useNavigate()
    const clerk = useClerk()
    const { isLoaded, user } = useUser()
    const { signIn } = useSignIn()
    const { signUp } = useSignUp()
    const { createToast } = useFixedSettings()
    const [loadingProvider, setLoadingProvider] = useState<string>()
    const [isReloadingUser, setIsReloadingUser] = useState(true)
    const [postReturnProvider, setPostReturnProvider] = useState<SupportedOAuthProvider | null>(() => readConnectedAccountLinkAttempt())
    const [linkError, setLinkError] = useState<ConnectedAccountErrorState | null>(null)
    const [isLinkErrorModalOpen, setIsLinkErrorModalOpen] = useState(false)

    useEffect(() => {
        if (!isLoaded || !user) return

        let cancelled = false
        setIsReloadingUser(true)
        user.reload()
            .catch((error) => console.error('Failed to reload user:', error))
            .finally(() => {
                if (!cancelled) setIsReloadingUser(false)
            })

        return () => {
            cancelled = true
        }
    }, [isLoaded, user])

    const connectedProviders = useMemo(() => (
        new Set(
            (user?.externalAccounts || [])
                .filter((account) => account.verification?.status === 'verified')
                .map((account) => account.provider)
        )
    ), [user?.externalAccounts])

    useEffect(() => {
        if (!isLoaded || !user || isReloadingUser || !postReturnProvider) return

        if (connectedProviders.has(postReturnProvider)) {
            clearConnectedAccountLinkAttempt()
            setPostReturnProvider(null)
            return
        }

        const attemptedProviderConfig = supportedOAuthProviders.find((provider) => provider.provider === postReturnProvider)
        const attemptedLabel = attemptedProviderConfig?.label || 'This account'
        const attemptedAccount = user.externalAccounts.find((account) => account.provider === postReturnProvider)
        const verification = getExternalAccountVerification(attemptedAccount)
        const verificationError = verification?.error ?? null
        const errorCode = verificationError?.code ?? null
        const specificMessage = getExternalAccountErrorMessage(postReturnProvider, errorCode)
        const fallbackErrorMessage = getVerificationErrorFallbackMessage(verificationError)

        clearConnectedAccountLinkAttempt()
        setPostReturnProvider(null)
        setLinkError({
            provider: postReturnProvider,
            message: specificMessage
                || fallbackErrorMessage
                || `${attemptedLabel} couldn't be connected. `
                    + `It may already be linked to another Splitcard account. `
                    + `Try a different ${attemptedProviderConfig?.label || 'social'} account, `
                    + `or sign in with the account that's already connected.`
        })
        setIsLinkErrorModalOpen(true)
    }, [connectedProviders, isLoaded, isReloadingUser, postReturnProvider, user])

    // Apple on native iOS: use the native Apple SDK (same as sign-in) instead
    // of Clerk's createExternalAccount OAuth redirect, which never completes
    // the verification callback back into the Capacitor app.
    //
    // Flow: get Apple identity token via Face ID → temporarily clear the Clerk
    // session (signIn.create rejects with session_exists otherwise) → create a
    // sign-in attempt with the token → Clerk links the Apple account to the
    // matching user → restore the session.
    async function connectAppleNative() {
        if (!signIn || !signUp || !user) throw new Error('Auth Services have yet to load.')

        const response = await SignInWithApple.authorize({
            clientId: import.meta.env.VITE_APPLE_SIGN_IN_CLIENT_ID,
            redirectURI: import.meta.env.VITE_APPLE_SIGN_IN_REDIRECT_URI,
            scopes: 'email name',
            state: createNonce(),
            nonce: createNonce(),
        })

        const identityToken = response.response?.identityToken
        if (!identityToken) throw new Error('Apple sign in did not return an identity token.')

        // signIn.create() rejects with "session_exists" when a session is
        // active. clerk.setActive({ session: null }) only clears it locally —
        // the server still sees the session via __clerk_db_jwt. We must
        // actually sign out to destroy the server-side session, then create a
        // new sign-in with the Apple token (which re-creates a session for
        // the same user and links the Apple account as a side effect).
        // Tell the auth layout to redirect back here after re-authentication.
        window.sessionStorage.setItem('post-apple-link-return', '/account/profile/connected-accounts')
        await clerk.signOut()

        try {
            const result = await signIn.create({
                strategy: 'oauth_token_apple',
                token: identityToken,
            } as Parameters<typeof signIn.create>[0])

            if (result.createdSessionId) {
                await clerk.setActive({ session: result.createdSessionId })
            } else if (result.firstFactorVerification?.status === 'transferable') {
                await signUp.create({ transfer: true })
                if (signUp.createdSessionId) {
                    await clerk.setActive({ session: signUp.createdSessionId })
                }
            }

            await user.reload()
            createToast({ type: 'success', text: 'Apple connected.' })
        } catch (error) {
            // If signIn.create fails after sign-out, the user is signed out.
            // The auth layout will redirect them to sign-in.
            throw error
        }
    }

    async function handleToggle(provider: typeof supportedOAuthProviders[number], nextChecked: boolean) {
        try {
            if (!user) throw new Error('Auth Services have yet to load.')

            setLoadingProvider(provider.provider)
            if (nextChecked) {
                const staleAccounts = user.externalAccounts.filter((account) => (
                    account.provider === provider.provider
                    && account.verification?.status !== 'verified'
                ))

                await Promise.all(staleAccounts.map((account) => account.destroy()))

                // Apple on native: use the native SDK flow instead of Clerk's
                // OAuth redirect which can't complete the callback on Capacitor.
                if (provider.provider === 'apple' && platform !== 'web') {
                    await connectAppleNative()
                    return
                }

                // On native, use the app's custom URL scheme for the callback.
                const nativeOrigin = import.meta.env.DEV
                    ? 'com.hanndev.splitcard.dev://'
                    : 'com.hanndev.splitcard://'
                const callbackUrl = platform === 'web'
                    ? window.location.href
                    : `${nativeOrigin}sso-callback?returnTo=${encodeURIComponent('/account/profile/connected-accounts')}`

                const externalAccount = await user.createExternalAccount({
                    strategy: provider.strategy,
                    redirectUrl: callbackUrl
                })

                const redirectUrl = externalAccount.verification?.externalVerificationRedirectURL?.href
                if (redirectUrl) {
                    writeConnectedAccountLinkAttempt(provider.provider)
                    if (platform === 'web') {
                        window.location.assign(redirectUrl)
                    } else {
                        window.open(redirectUrl, '_blank')
                    }
                    return
                }

                await user.reload()
                createToast({ type: 'success', text: `${provider.label} connected.` })
                return
            }

            const externalAccounts = user.externalAccounts.filter((account) => account.provider === provider.provider)
            if (externalAccounts.length === 0) throw new Error(`${provider.label} is not connected.`)

            await Promise.all(externalAccounts.map((account) => account.destroy()))
            await user.reload()
            createToast({ type: 'success', text: `${provider.label} disconnected.` })
        } catch (error: unknown) {
            // Don't show an error if the user cancelled the Apple Face ID sheet.
            if (isUserCancelledAppleSignIn(error)) return

            console.error(error)
            createToast({
                type: 'error',
                text: getErrorMessage(error, `Unable to update ${provider.label}.`)
            })
        } finally {
            setLoadingProvider(undefined)
        }
    }

    return (<>
        <ErrorModal
            state={[isLinkErrorModalOpen, setIsLinkErrorModalOpen]}
            title="Account Couldn't Be Connected"
            confirmLabel='Got It'
            fallbackMessage={linkError?.message}
            onClose={() => {
                setLinkError(null)
                setIsLinkErrorModalOpen(false)
            }}
        />
        <div className='flex flex-row items-center gap-4'>
            <a onClick={() => navigate('../')}>
                <img src='/icons/arrow-left.svg' alt='back' style={{ width: '1.4rem', height: '1.4rem' }} draggable='false' />
            </a>
            <h1>Connected Accounts</h1>
        </div>
        <div className='flex flex-col gap-4'>
            {supportedOAuthProviders.map((provider) => {
                const isConnected = connectedProviders.has(provider.provider)
                const isLoading = loadingProvider === provider.provider

                return (
                    <Card key={provider.provider} className='gap-2'>
                        <div className='flex flex-row items-center justify-between gap-4'>
                            <div className='flex flex-row items-center gap-4'>
                                <div className='bubble-xsml flex items-center justify-center !bg-gray-100'>
                                    {provider.iconSrc
                                        ? <img src={provider.iconSrc} alt={`${provider.label} logo`} style={{ width: '1.25rem', height: '1.25rem', objectFit: 'contain' }} />
                                        : <strong>{provider.label.at(0)}</strong>
                                    }
                                </div>
                                <div className='flex flex-col'>
                                    <h4>{provider.label}</h4>
                                    <p className={isConnected ? 'text-green-600 font-bold' : 'text-gray-500'}>
                                        {isLoading ? 'Updating...' : (isConnected ? 'Connected' : 'Not connected')}
                                    </p>
                                </div>
                            </div>
                            {isLoading
                                ? <Spinner indicator='circle' />
                                : (
                                    <Switch
                                        checked={isConnected}
                                        disabled={!!loadingProvider}
                                        onChange={(event) => handleToggle(provider, event.currentTarget.checked)}
                                    />
                                )
                            }
                        </div>
                    </Card>
                )
            })}
        </div>
        <p className='text-body-2 text-center text-gray-500'>
            Connecting an account lets you sign in faster next time. You can disconnect it at any time.
        </p>
    </>)
}

const ConnectedAccountsRoute: RouteObject = {
    path: 'connected-accounts',
    Component,
}

export { ConnectedAccountsRoute }
